import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import aiimage from "../assets/images/ai_ml.jpg";
import iotimage from "../assets/images/iot.jpg";
import roboticsimage from "../assets/images/robotics.jpg";
import promptengineeringimage from "../assets/images/prompt_engineering.jpg";
import Swal from "sweetalert2";
import LoginModal, { StaticBackdrop } from "./LoginModal";

/* ═══════════════════════════════════════════════
   COURSE THUMBNAIL — image slideshow OR video
   - Fixed 16:9 box (via CSS) so no card can ever
     grow taller than another because of its media.
   - media_type === "video": plays the video (muted,
     looped, autoplay, no controls) filling the thumb.
   - media_type === "image" with multiple images:
     auto-advances through them every few seconds.
   - single image: renders like a plain <img>.
═══════════════════════════════════════════════ */
function CourseThumb({ course, getImage }) {
  const images = Array.isArray(course.images) && course.images.length
    ? course.images
    : (course.image ? [course.image] : []);

  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (course.media_type === "video") return; // no slideshow for video
    if (images.length <= 1) return; // nothing to slide

    const interval = setInterval(() => {
      setSlide((s) => (s + 1) % images.length);
    }, 3500);

    return () => clearInterval(interval);
  }, [images.length, course.media_type]);

  if (course.media_type === "video" && course.video) {
    return (
      <video
        className="si-course-thumb-media"
        src={`https://swiftintelli.com/api/uploads/${course.video}`}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
    );
  }

  if (images.length > 0) {
    const src = images[slide]?.startsWith("http")
      ? images[slide]
      : `https://swiftintelli.com/api/uploads/${images[slide]}`;

    return (
      <>
        <img
          className="si-course-thumb-media"
          src={src}
          alt="{course.title}"
          loading="lazy"
        />

        {images.length > 1 && (
          <div className="si-course-thumb-dots">
            {images.map((_, i) => (
              <span
                key={i}
                className={`si-course-thumb-dot ${i === slide ? "active" : ""}`}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  // No image or video at all — fall back to the bundled placeholder art
  return (
    <img
      className="si-course-thumb-media"
      src={getImage(course.image)}
      alt="{course.title}"
      loading="lazy"
    />
  );
}

/* ==========================================
   INTERSECTION OBSERVER
========================================== */

function useInView(ref) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
      }
    );

    const element = ref.current;

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
      observer.disconnect();
    };
  }, []);

  return visible;
}


/* ==========================================
   ANIMATED COURSE CARD
========================================== */

function AnimCard({
  className,
  children,
  delay = 0,
  onClick,
}) {
  const ref = useRef(null);
  const visible = useInView(ref);

  return (
    <div
      ref={ref}
      className={`${className} ${visible ? "visible" : ""}`}
      style={{
        ...(visible
          ? { transitionDelay: `${delay}ms` }
          : {}),
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN COURSES COMPONENT
═══════════════════════════════════════════════ */
function Courses({
  onEnrollNow,
  showViewAll = true,
  showAllCourses = false,
  showHeading = true
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [seconds, setSeconds] = useState(120);
  const [userForm, setUserForm] = useState({
    name: "",
    countryCode: "+91",
    phone: "",
    email: "",
  });
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
const displayedCourses = showAllCourses
  ? courses
  : courses.slice(0, 3);
  const getImage = (img) => {
    switch (img) {
      case "ai_ml.jpg":              return aiimage;
      case "iot.jpg":                return iotimage;
      case "robotics.jpg":           return roboticsimage;
      case "prompt_engineering.jpg": return promptengineeringimage;
      default:                       return aiimage;
    }
  };

  /* OTP countdown */
  useEffect(() => {
    if (!showOtp || seconds <= 0) return;
    const timer = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [showOtp, seconds]);

  /* Lock scroll when modal is open */
  useEffect(() => {
    if (showLogin) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [showLogin]);

  /* Fetch courses */
  useEffect(() => {
    fetch("https://swiftintelli.com/api/getCourses.php")
      .then(async (res) => {
        const t = await res.text();
        try { return JSON.parse(t); } catch { return []; }
      })
      .then((d) => { setCourses(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const getCourseSlug = (course) => {
  if (!course) return "";

  // Use the slug coming from API first
  if (course.slug) {
    return String(course.slug).trim();
  }

  // Other possible API field names
  if (course.course_slug) {
    return String(course.course_slug).trim();
  }

  if (course.courseSlug) {
    return String(course.courseSlug).trim();
  }

  // Last fallback: create slug from course name
  if (course.name) {
    return String(course.name)
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  return "";
};

const handleEnroll = async (course) => {
  if (!course) return;

  // Upcoming courses should not open course details
  if (course.type === "upcoming") {
    return;
  }

  const slug = getCourseSlug(course);

  console.log("Selected Course:", course);
  console.log("Resolved Course Slug:", slug);

  if (!slug) {
    Swal.fire({
      icon: "error",
      title: "Course URL Error",
      text: "Unable to determine the course URL. Please contact the administrator.",
    });

    return;
  }

  setSelectedSlug(slug);

  const email = localStorage.getItem("email");

  // No login found
  if (!email) {
    setShowLogin(true);
    return;
  }

  // Check whether email still exists in database
  const fd = new FormData();
  fd.append("email", email);

  try {
    const response = await fetch(
      "https://swiftintelli.com/api/checkUser.php",
      {
        method: "POST",
        body: fd,
      }
    );

    const result = await response.json();

    if (result.exists) {
      localStorage.setItem("loggedIn", "true");

      navigate(`/courses/${slug}`);

      return;
    }

    // User no longer exists
    localStorage.removeItem("email");
    localStorage.removeItem("loggedIn");

    setShowLogin(true);
  } catch (error) {
    console.error("Failed to verify user:", error);

    Swal.fire({
      icon: "error",
      title: "Something went wrong",
      text: "Unable to verify your account. Please try again.",
    });
  }
};

  const closeModal = useCallback(() => {
    setShowLogin(false);
    setShowOtp(false);
    setOtp("");
    setSeconds(120);
    setUserForm({ name: "", countryCode: "+91", phone: "", email: "" });
  }, []);

  const sendOtp = useCallback(async () => {
    if (!userForm.name.trim() || !userForm.phone.trim() || !userForm.email.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Fields", text: "All fields are mandatory" });
      return;
    }

    if (userForm.countryCode === "+91" && userForm.phone.length !== 12) {
      Swal.fire({ icon: "error", title: "Invalid Phone Number", text: "Indian number should contain exactly 10 digits" });
      return;
    }

    const emailRegex = /^(?!.*\.\.)(?!.*@.*@)[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(userForm.email.trim())) {
      Swal.fire({ icon: "error", title: "Invalid Email", text: "Please enter a valid email address" });
      return;
    }

    const fd = new FormData();
    fd.append("name", userForm.name);
    fd.append("countryCode", userForm.countryCode);
    fd.append("phone", userForm.phone);
    fd.append("email", userForm.email);

    const res = await fetch(
      "https://swiftintelli.com/api/sendOtp.php",
      {
        method: "POST",
        body: fd
      }
    );

    const data = await res.json();
    console.log("sendOtp Response:", data);

    // Already Registered — Direct login routing without locking them in an OTP retry trap
    if (data.exists || data.alreadyRegistered) {
      localStorage.setItem("loggedIn", "true");
      localStorage.setItem("email", userForm.email);
      localStorage.setItem("name", userForm.name); // <-- Add this line
      Swal.fire({
        icon: "info",
        title: "Already Registered!",
        text: "You already have an account with this email. Logging you in...",
        timer: 2000,
        showConfirmButton: false,
      }).then(() => {
        closeModal();
        navigate(`/courses/${selectedSlug}`);
      });
      return;
    }

    // New User / OTP Success
    if (data.success) {
      Swal.fire({
        icon: "success",
        title: "OTP Sent",
        text: data.message,
        timer: 2000,
        showConfirmButton: false,
      });

      setShowOtp(true);
      setOtp("");
      setSeconds(120);
    } else {
      Swal.fire({
        icon: "warning",
        title: "Notice",
        text: data.message || "An OTP was already requested recently or account exists. Please check your inbox."
      });
    }
  }, [userForm, selectedSlug, closeModal, navigate]);

  const verifyOtp = useCallback(async () => {
    const fd = new FormData();
    fd.append("email", userForm.email);
    fd.append("otp", otp);

    const res = await fetch("https://swiftintelli.com/api/verifyOtp.php", { method: "POST", body: fd });
    const data = await res.json();
    if (data.success) {
      localStorage.setItem("loggedIn", "true");
      localStorage.setItem("email", userForm.email);

      Swal.fire({
        icon: "success",
        title: "Welcome!",
        text: data.message,
        timer: 2000,
        showConfirmButton: false,
      }).then(() => {
        closeModal();
        navigate(`/courses/${selectedSlug}`);
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: data.message || "Invalid OTP"
      });
    }
  }, [userForm, otp, selectedSlug, closeModal, navigate]);

  const handleResend = useCallback(async () => {
    await sendOtp();
    setOtp("");
    setSeconds(120);
  }, [sendOtp]);

  const filtered = filter === "all" ? courses : courses.filter((c) => c.type === filter);

  // /* Intersection observer hook */
  // function useInView(ref) {
  //   const [visible, setVisible] = useState(false);
  //   useEffect(() => {
  //     const obs = new IntersectionObserver(
  //       ([e]) => e.isIntersecting && setVisible(true),
  //       { threshold: 0.1 }
  //     );
  //     if (ref.current) obs.observe(ref.current);
  //     return () => obs.disconnect();
  //   }, [ref]);
  //   return visible;
  // }

  // function AnimCard({ className, children, delay = 0, onClick }) {
  //   const ref = useRef(null);
  //   const visible = useInView(ref);
  //   return (
  //     <div
  //       ref={ref}
  //       className={`${className} ${visible ? "visible" : ""}`}
  //       style={{
  //         ...(visible ? { transitionDelay: `${delay}ms` } : {}),
  //         cursor: "pointer",
  //       }}
  //       onClick={onClick}
  //     >
  //       {children}
  //     </div>
  //   );
  // }

  return (
    <section id="courses" className="si-section si-courses">
      {/* HEADER */}
      <div className="si-courses-header">
       {showHeading && ( <div>
          <div className="si-section-label">Our Programs</div>
          <h2 className="si-h2">
            Courses <span style={{ color: "var(--accent)" }}>Offered</span>
          </h2>
        </div>)}
        <div className="si-tab-filters">
          {["all", "live", "upcoming"].map((f) => (
            <button
              key={f}
              className={`si-tab-btn ${filter === f ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setFilter(f);
              }}
            >
              {f === "all" ? "All" : f === "live" ? "Live Now" : "Upcoming"}
            </button>
          ))}
        </div>
      </div>

      {loading && <h3 style={{ textAlign: "center" }}>Loading...</h3>}

      <div className="si-courses-grid">
        {!loading && courses.length === 0 && (
          <p style={{ textAlign: "center" }}>❌ No data from API</p>
        )}
       {(showAllCourses
  ? filtered
  : filtered.slice(0, 3)
).map((c, i) => (
         <AnimCard
  key={c.id}
  className={`si-course-card ${
    c.type === "upcoming" ? "si-course-upcoming" : ""
  }`}
  delay={i * 80}
 onClick={() => {
  if (c.type !== "upcoming") {
    handleEnroll(c);
  }
}}
>
            <div className="si-course-thumb">
              <CourseThumb course={c} getImage={getImage} />
              <span className={`si-course-badge ${c.type}`}>
                {c.type === "live" ? "ONGOING" : "COMING SOON"}
              </span>
            </div>
            <div className="si-course-body">
              <div className="si-course-name">{c.name}</div>
              <div className="si-course-desc">{c.description}</div>
              <div className="si-course-footer">
                <div>⏱ {c.duration}</div>
                <div>🌐 Online &amp; Offline</div>
                <div>
                 {c.type === "upcoming" ? (
  <button
    className="si-enroll-btn si-coming-soon-btn"
    disabled
  >
    Coming Soon
  </button>
) : (
  <button
    className="si-enroll-btn"
   onClick={(e) => {
  e.stopPropagation();
  handleEnroll(c);
}}
  >
    View More →
  </button>
)}
                </div>
              </div>
            </div>
          </AnimCard>
        ))}
      </div>

     {showViewAll && !showAllCourses && (
  <div className="si-courses-footer">
    <Link
      to="/courses"
      className="si-view-more-btn"
    >
      <span>View All Courses</span>
      <span aria-hidden="true">→</span>
    </Link>
  </div>
)}

      
      {/* Login modal — two separate portals so backdrop never re-renders on input */}
      {showLogin && <StaticBackdrop />}
      {showLogin && (
        <LoginModal
          showOtp={showOtp}
          userForm={userForm}
          setUserForm={setUserForm}
          otp={otp}
          setOtp={setOtp}
          seconds={seconds}
          onClose={closeModal}
          onSendOtp={sendOtp}
          onVerifyOtp={verifyOtp}
          onResend={handleResend}
        />
      )}
    </section>
  );
}

export default Courses;